import {
  AgentThreadRole,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
  type AgentThreadRecord,
  type AgentThreadToolUseContent,
} from "./AgentThreadRecord";

/**
 * Returns the client-tool `tool_use` blocks that still need execution in a
 * thread that is in CLIENT_TOOL_TURN.
 *
 * Finds the latest assistant message by scanning back from the end, since the
 * last message may be a `roboto` message the server appended with results
 * for server-side tool uses that ran in the same turn. Collects its tool_uses
 * and filters out any whose `tool_use_id` already has a matching
 * `tool_result` in a subsequent `roboto` message. The remaining tool_uses
 * are the ones the client still owes results for.
 *
 * Also filters out tool_uses whose `tool_name` is not a known client tool.
 * In the mixed server+client turn the thread can flip to CLIENT_TOOL_TURN
 * before the backend has finished persisting every server tool_result, so
 * an unanswered server tool_use briefly looks "pending" from the browser's
 * view of the delta. Submitting a client-side error result for it would
 * land a second tool_result for the same id once the backend's real
 * tool_result arrives, and Bedrock rejects the next Converse call
 * ("The number of toolResult blocks … exceeds the number of toolUse
 * blocks"). Leave those for the backend to answer.
 *
 * Also filters out tool_uses flagged `answered_by_server`, whose results the
 * server writes itself. A `/delta` poll that brings no new rows reads the
 * thread status in a separate query from the messages, so the browser can see
 * CLIENT_TOOL_TURN before it holds the server's result for such a tool_use.
 * The server sets the flag when it first writes the tool_use, so the browser
 * never holds one without it.
 */
export function findPendingClientToolUses(
  agentThread: AgentThreadRecord,
  isClientTool: (toolName: string) => boolean,
): AgentThreadToolUseContent[] {
  let assistantIdx = -1;
  for (let i = agentThread.messages.length - 1; i >= 0; i -= 1) {
    if (agentThread.messages[i].role === AgentThreadRole.ASSISTANT) {
      assistantIdx = i;
      break;
    }
  }
  if (assistantIdx === -1) {
    return [];
  }

  const toolUses = agentThread.messages[assistantIdx].content.filter(
    isAgentThreadToolUseContent,
  );
  if (toolUses.length === 0) {
    return [];
  }

  const resolvedIds = new Set<string>();
  for (let i = assistantIdx + 1; i < agentThread.messages.length; i += 1) {
    const msg = agentThread.messages[i];
    if (msg.role !== AgentThreadRole.ROBOTO) {
      continue;
    }
    for (const block of msg.content) {
      if (isAgentThreadToolResultContent(block)) {
        resolvedIds.add(block.tool_use_id);
      }
    }
  }

  return toolUses.filter(
    (tu) =>
      !resolvedIds.has(tu.tool_use_id) &&
      !tu.answered_by_server &&
      isClientTool(tu.tool_name),
  );
}
